import { Box, Button, Card, Grid, styled, useTheme } from '@mui/material';
import { H5, Small } from 'components/Typography';
import { Bookmark, RefreshCcwIcon, Share2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import FlexBox from 'components/flexbox/FlexBox';
import ReversePagination from 'components/ReversePagination';
import FilterButtons from '../FilterButtons';

// Empty state component
const EmptyState = ({ isMobile, onButtonClick }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '20px 10px' : '60px 40px',
        textAlign: 'center',
      }}
    >
      {/* Banknote Icon */}
      <Box
        sx={{
          width: isMobile ? '80px' : '100px',
          height: isMobile ? '80px' : '100px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src="/static/icons/ticket.svg"
          alt="ticket"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Korean Text */}
      <H5
        sx={{
          color: theme.palette.text.secondary,
          fontSize: isMobile ? '16px' : '18px',
          fontWeight: '500',
          lineHeight: 1.4,
          marginBottom: '32px',
          maxWidth: '300px',
        }}
      >
        기업정보를 입력하면 AI를 통해 자동으로 검색하고 추천받을 수 있어요!
      </H5>

      {/* Button */}
      <Button
        sx={{
          backgroundColor: theme.palette.primary.mainBlue,
          color: '#fff',
          borderRadius: '25px',
          padding: isMobile ? '12px 24px' : '14px 28px',
          fontSize: isMobile ? '14px' : '16px',
          fontWeight: '600',
          textTransform: 'none',
          border: 'none',
          minWidth: 'auto',
          '&:hover': {
            backgroundColor: theme.palette.primary.mainBlue,
            opacity: 0.9,
            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
          },
          transition: 'all 0.2s ease-in-out',
        }}
        onClick={onButtonClick}
      >
        기업 정보 입력
      </Button>
    </Box>
  );
};

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  padding: 0,
  backgroundColor: theme.palette.primary.lightGrey200,
  borderRadius: 0,
  border: 'none',
  [theme.breakpoints.down('sm')]: {
    '& > .MuiBox-root': {
      paddingBottom: 0,
    },
  },
}));

const FundingSection = ({
  isTablet,
  isMobile,
  fundingData = [],
  title = '정부지원금',
  icon = '/static/icons/money.svg',
  filterItems = ['전체', '금융', '기술', '인력', '수출', '내수', '창업'],
  onAIRecommendClick,
  onCardClick,
  onEmptyStateButtonClick,
  showAIRecommendButton = true,
  showMoreButton = true,
  moreButtonText = '최신정보 보기',
  ...props
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState('전체');
  const [bookmarkedItems, setBookmarkedItems] = useState(() => {
    // Initialize bookmarked items from the data
    const initialBookmarks = new Set();
    fundingData.forEach(item => {
      if (item.bookmarked) {
        initialBookmarks.add(item.id);
      }
    });
    return initialBookmarks;
  });

  const dataToUse = fundingData;

  const toggleBookmark = itemId => {
    setBookmarkedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleCardClick = item => {
    if (onCardClick) {
      onCardClick(item);
    } else {
      navigate('/naver/callmetellme/jiwongum/post-detail');
    }
  };

  const handleAIRecommendClick = () => {
    if (onAIRecommendClick) {
      onAIRecommendClick();
    } else {
    }
  };

  const handleEmptyStateButtonClick = () => {
    if (onEmptyStateButtonClick) {
      onEmptyStateButtonClick();
    } else {
      console.log('회사 정보 입력하기 clicked');
    }
  };

  return (
    <StyledCard
      sx={{
        border: 'none',
        overflow: 'visible',
        ...props.sx,
      }}
    >
      <Box
        sx={{
          width: '100%',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: '#fff',
            padding: isMobile ? '18px' : '26px',
            borderRadius: '24px',
            boxShadow: '1px 4px 6px rgba(0, 0, 0, 0.05)',
          }}
        >
          {/* Show header elements only when there's data */}
          {dataToUse.length > 0 && (
            <>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                mb={1}
              >
                {showAIRecommendButton && (
                  <Button
                    sx={{
                      background:
                        'linear-gradient(90deg, #3F68FF 0%, #9A68FF 100%)',
                      color: '#fff',
                      borderRadius: '25px',
                      padding: isMobile ? '6px 14px' : '8px 16px',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '600',
                      textTransform: 'none',
                      border: 'none',
                      minWidth: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      '&:hover': {
                        background:
                          'linear-gradient(40deg, #3F68FF 0%, #9A68FF 100%)',
                        boxShadow: '0 6px 16px rgba(79, 70, 229, 0.2)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                    onClick={handleAIRecommendClick}
                  >
                    <RefreshCcwIcon size={20} />
                    <Small
                      sx={{
                        fontSize: isMobile ? '13px' : '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        color: '#fff',
                      }}
                    >
                      다시 추천 받기
                    </Small>
                  </Button>
                )}
              </Box>

              {/* 필터 버튼들 */}
              <FilterButtons
                menuItems={filterItems}
                selectedFilter={selectedFilter}
                onFilterChange={setSelectedFilter}
              />
            </>
          )}

          {/* Empty State or Funding Cards */}
          {dataToUse.length === 0 ? (
            <EmptyState
              isMobile={isMobile}
              onButtonClick={handleEmptyStateButtonClick}
            />
          ) : (
            <>
              <Grid container spacing={isMobile ? 3 : 3}>
                {dataToUse.map((item, index) => (
                  <Grid
                    onClick={() => handleCardClick(item)}
                    item
                    xs={12}
                    md={6}
                    key={item.id}
                  >
                    <Box
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: '#fcfcfc',
                        borderRadius: '24px',
                        padding: isTablet ? '16px' : '20px',
                        border: `1.5px solid ${theme.palette.primary.grey10}`,
                        transition: 'all 0.2s ease-in-out',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': isMobile
                          ? {}
                          : {
                              transform: 'translateY(-2px)',
                            },
                      }}
                    >
                      {/* Card Header */}
                      <Box flex={1}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={isMobile ? 1 : 1.5}
                          mb={isMobile ? 1 : 1.5}
                          flexWrap="wrap"
                        >
                          {item.tags &&
                            item.tags.map((tag, tagIndex) => (
                              <Box
                                key={tagIndex}
                                sx={{
                                  backgroundColor: '#E3F0FF',
                                  color: theme.palette.primary.mainBlue,
                                  padding: isMobile ? '2px 8px' : '4px 12px',
                                  borderRadius: '100px',
                                  fontSize: isMobile ? '12px' : '13px',
                                  fontWeight: '500',
                                }}
                              >
                                {tag}
                              </Box>
                            ))}
                        </Box>
                      </Box>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        flexDirection="row"
                        mb={isMobile ? 2 : 3}
                      >
                        <Box
                          sx={{
                            width: isMobile ? '44px' : '50px',
                            height: isMobile ? '44px' : '50px',
                            backgroundColor: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: isMobile ? '12px' : '16px',
                            flexShrink: 0,
                            border: `1px solid ${theme.palette.primary.borderColor}`,
                            borderRadius: '100px',
                          }}
                        >
                          <img
                            src={item.logo || '/static/logo/SMEs.png'}
                            alt="logo"
                            style={{
                              width: isMobile ? '48px' : '52px',
                              borderRadius: '100px',
                            }}
                          />
                        </Box>
                        <H5
                          sx={{
                            fontWeight: 'bold',
                            lineHeight: 1.3,
                            marginBottom: '8px',
                            fontSize: isMobile ? '16px' : '18px',
                          }}
                        >
                          {item.title}
                        </H5>
                      </Box>

                      {/* AI 요약 */}
                      <Box mb={isMobile ? 2 : 3} sx={{ flexGrow: 1 }}>
                        <Box
                          sx={{
                            backgroundColor: '#ffffff',
                            padding: '16px',
                            borderRadius: '24px',
                            boxShadow: theme.palette.shadows.cardBoxShadow,
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={isMobile ? 1 : 1.5}
                          >
                            <Box
                              sx={{
                                width: isMobile ? '24px' : '28px',
                                height: isMobile ? '24px' : '28px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <img
                                src="/static/icons/magic-wand.svg"
                                style={{
                                  width: isMobile ? '26px' : '32px',
                                  height: isMobile ? '26px' : '32px',
                                }}
                                alt="magic wand"
                              />
                            </Box>
                            <H5
                              sx={{
                                color: theme.palette.primary.mainBlue,
                                fontSize: isMobile ? '16px' : '18px',
                              }}
                            >
                              {item.category || 'AI 한줄 요약'}
                            </H5>
                          </Box>

                          {item.description &&
                            item.description.split('\n').map((line, index) => (
                              <Box
                                key={index}
                                display="flex"
                                alignItems="flex-start"
                                mb={0.5}
                              >
                                <Box
                                  sx={{
                                    width: '4px',
                                    height: '4px',
                                    backgroundColor:
                                      theme.palette.text.secondary,
                                    borderRadius: '50%',
                                    marginTop: '6px',
                                    marginRight: '8px',
                                    flexShrink: 0,
                                  }}
                                />
                                <Small
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.4,
                                    fontSize: isMobile ? '14px' : '15px',
                                  }}
                                >
                                  {line}
                                </Small>
                              </Box>
                            ))}
                        </Box>
                      </Box>

                      {/* Footer */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          flexWrap: isMobile ? 'wrap' : 'nowrap',
                          gap: isMobile ? 2 : 3,
                          marginTop: 'auto',
                        }}
                      >
                        <FlexBox
                          sx={{
                            minWidth: isMobile ? 'auto' : '120px',
                            alignItems: 'center',
                          }}
                        >
                          <Small
                            sx={{
                              color: theme.palette.text.secondary,
                              marginRight: '12px',
                            }}
                          >
                            지원 금액
                          </Small>
                          <Small
                            sx={{
                              fontWeight: 'bold',
                              color: theme.palette.text.primary,
                            }}
                          >
                            {item.amount}
                          </Small>
                        </FlexBox>
                        <Box
                          textAlign="right"
                          sx={{ minWidth: isMobile ? 'auto' : '100px' }}
                        >
                          <Small
                            sx={{
                              color: theme.palette.text.secondary,
                              marginRight: '12px',
                            }}
                          >
                            신청 기간
                          </Small>
                          <Small
                            sx={{
                              fontWeight: 'bold',
                              color: theme.palette.text.primary,
                            }}
                          >
                            {item.deadline}
                          </Small>
                        </Box>
                        <Box display="flex" gap={isMobile ? 1 : 1.5}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                backgroundColor: '#f5f5f5',
                                transform: isMobile ? 'none' : 'scale(1.05)',
                              },
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              toggleBookmark(item.id);
                            }}
                          >
                            <Bookmark
                              size={isMobile ? 19 : 21}
                              color={
                                bookmarkedItems.has(item.id)
                                  ? theme.palette.primary.mainBlue
                                  : theme.palette.primary.greytxt
                              }
                              fill={
                                bookmarkedItems.has(item.id)
                                  ? theme.palette.primary.mainBlue
                                  : 'none'
                              }
                            />
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                backgroundColor: '#f5f5f5',
                                transform: isMobile ? 'none' : 'scale(1.05)',
                              },
                            }}
                            onClick={e => e.stopPropagation()}
                          >
                            <Share2
                              size={isMobile ? 19 : 21}
                              color={theme.palette.primary.greytxt}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {dataToUse.length > 4 && (
                <FlexBox
                  width="100%"
                  justifyContent={'center'}
                  pt={isTablet ? 2 : 2}
                >
                  <ReversePagination />
                </FlexBox>
              )}
            </>
          )}
        </Box>
      </Box>
    </StyledCard>
  );
};

export default FundingSection;
